import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Chip, InputAdornment, TextField } from "@material-ui/core";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import CloseIcon from "@material-ui/icons/Close";
import { normalizeAgreementCode } from "../../../utils/pharmacyValidation";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fcfdfe",
      transition: "background 160ms, box-shadow 160ms",
      "&.Mui-focused": {
        background: "#fff",
      },
    },
  },
  adornment: {
    color: "#8fa6b7",
    "&.Mui-focused": {
      color: "#0077B6",
    },
  },
  helper: {
    marginTop: 6,
    marginLeft: 2,
    fontSize: "0.75rem",
    lineHeight: 1.45,
    color: "#8497a6",
  },
  helperError: {
    color: "#c62828",
    fontWeight: 600,
  },
  codeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  codeChip: {
    height: 30,
    fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
    fontSize: "0.8125rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: "#085279",
    background: "#eaf3fa",
    border: "1px solid #cfe4f2",
    "& .MuiChip-deleteIcon": {
      color: "#7b8fa0",
      fontSize: 16,
      "&:hover": {
        color: "#c62828",
      },
    },
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

/**
 * The agreement code (código de convenio) of a place.
 *
 * The code is normalized while the admin types it, it is optional and it can
 * be pushed to other places of the same franchise with the secondary button.
 */
export default function AgreementCodeField({
  value,
  onChange,
  error,
  t,
  onApplyToOthers,
}) {
  const classes = useStyles();
  const normalized = normalizeAgreementCode(value) || "";

  const handleChange = (event) => {
    onChange(normalizeAgreementCode(event.target.value));
  };

  const handleClear = () => {
    onChange(null);
  };

  return (
    <div className={classes.root}>
      <TextField
        label={t("Agreement code")}
        variant="outlined"
        fullWidth
        value={value || ""}
        onChange={handleChange}
        error={!!error}
        className={classes.input}
        name="agreement_code"
        autoComplete="off"
        helperText={
          <span
            className={`${classes.helper} ${error ? classes.helperError : ""}`}
          >
            {error ||
              t(
                "Code of the agreement or discount that the patient can use in this place. Optional."
              )}
          </span>
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocalOfferIcon fontSize="small" className={classes.adornment} />
            </InputAdornment>
          ),
        }}
      />

      {normalized !== "" && (
        <div className={classes.codeRow}>
          <Chip
            label={normalized}
            onDelete={handleClear}
            deleteIcon={<CloseIcon />}
            className={classes.codeChip}
            aria-label={t("Remove agreement code")}
          />
        </div>
      )}

      {onApplyToOthers && (
        <div className={classes.actions}>
          <Button
            variant="outlined"
            color="default"
            size="small"
            onClick={onApplyToOthers}
            startIcon={<LocalOfferIcon />}
          >
            {t("Apply to other places")}
          </Button>
        </div>
      )}
    </div>
  );
}
